import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { LanguageToggle } from "../../components/LanguageToggle";
import SliderCaptcha from "../../components/SliderCaptcha";
import { currentProfile, loginWithPassword } from "../../services/sessionAuth";
import { isSuperAdmin, rolesOf } from "../../utils/authorization";

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);

  const resetCaptcha = () => {
    setCaptchaVerified(false);
    setCaptchaKey((key) => key + 1);
  };

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!captchaVerified) {
      setError(t("login.captchaRequired", { defaultValue: "Please verify you are human" }));
      return;
    }
    setBusy(true);
    setError("");
    try {
      await loginWithPassword(username, password);
      const roles = rolesOf(currentProfile());
      navigate(isSuperAdmin(roles) ? "/admin/dashboard" : "/home", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("login.error"));
      resetCaptcha();
    } finally {
      setBusy(false);
    }
  }

  const handleCaptchaVerify = (verified: boolean) => {
    setCaptchaVerified(verified);
    if (verified) {
      setError("");
    }
  };

  const handleCaptchaReset = () => {
    setCaptchaVerified(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        p: 2,
        background:
          "radial-gradient(1000px 520px at 15% -10%, rgba(131,186,183,0.22), transparent 60%), radial-gradient(800px 480px at 95% 5%, rgba(141,160,96,0.12), transparent 55%), #F4F8F7",
      }}
    >
      <Box sx={{ position: "absolute", top: 20, right: 20 }}>
        <LanguageToggle />
      </Box>
      <Paper sx={{ p: { xs: 2.5, sm: 3.5 }, width: "100%", maxWidth: 440 }} elevation={0}>
        <Stack spacing={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              display: "grid",
              placeItems: "center",
            }}
          >
            <SupportAgentIcon />
          </Box>
          <Box>
            <Typography variant="h4" component="h1" fontWeight={700} gutterBottom sx={{ mb: 0.5 }}>
              {t("login.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("login.subtitle")}
            </Typography>
          </Box>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Box component="form" onSubmit={onSubmit} sx={{ display: "grid", gap: 2 }}>
            <TextField
              label={t("login.username")}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
            <TextField
              type="password"
              label={t("login.password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <SliderCaptcha
              key={captchaKey}
              onVerify={handleCaptchaVerify}
              onReset={handleCaptchaReset}
            />
            <Button type="submit" variant="contained" size="large" disabled={busy || !captchaVerified}>
              {t("login.submit")}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
